import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { StoreState } from '../../store/store.interface';
import { CustomerState } from './customer.interface';
import { defaultApiState } from '../../common/constants/api-constants';
import { ActionStatus } from '../../common/constants/action-status';
import { addAuthTokenToCookie } from '../../common/utils/util';
import getCookie, { COOKIE_MH_TOKEN } from '../../common/cookies';
import environment from '../../common/environment';
import { addResponseInteceptor } from '../../common/utils/api-utils';
import { mhecommSessionStorage } from '../../common/local-storage';


/*----- CREATE STORE ------*/
export const customerInitialState: CustomerState = {
    login: defaultApiState,
    customer: defaultApiState
  };

const customerSlice = createSlice({
    name: 'customer',
    initialState: customerInitialState,
    reducers: {
        clearLoginCustomerState(state) {
            state.login.data = undefined;
            state.login.status = ActionStatus.idle;
            state.customer.data = undefined;
            state.customer.status = ActionStatus.idle;
        }
    },
    extraReducers(builder) {
        builder
        .addCase(loginCustomer.pending, (state) => {
            state.login.status = ActionStatus.loading;
        })
        .addCase(loginCustomer.fulfilled, (state, action) => {
            state.login.status = ActionStatus.succeeded;
            state.login.data = action.payload;
        })
        .addCase(loginCustomer.rejected, (state, action) => {
            state.login.status = ActionStatus.failed;
            state.login.error = action.payload;
        })
        .addCase(loadCustomer.pending, (state) => {
            state.customer.status = ActionStatus.loading;
        })
        .addCase(loadCustomer.fulfilled, (state, action) => {
            state.customer.status = ActionStatus.succeeded;
            state.customer.data = action.payload;
        })
        .addCase(loadCustomer.rejected, (state, action) => {
            state.customer.status = ActionStatus.failed;
            state.customer.error = action.payload;
        })
        .addCase(resetLoginState, () => customerInitialState); // Resets to initial state
    },
});

export const { clearLoginCustomerState } = customerSlice.actions;


/*----- THUNKS ------*/
export const loginCustomer = createAsyncThunk("customer/login", async (_data, thunkAPI) => {
    try {
        return await personLoginToMagento();
      } catch (error) {
        return thunkAPI.rejectWithValue(error);
      } finally {
        // Todo Q. what to do if login fails 
      }
}, {
    condition: ((_arg, { getState }) => {
        const state = getState() as StoreState;
        return state.customerState.login.status === ActionStatus.idle;
    })
},);

let isMagentoAuthRefreshInterceptorAdded = false;
export const loadCustomer = createAsyncThunk(
  'customer/load',
  async (_data, thunkAPI) => {
    try {
      await thunkAPI.dispatch(loginCustomer());

      if (!isMagentoAuthRefreshInterceptorAdded) {
        addResponseInteceptor(
          async (response, fetchInstance) => {
            mhecommSessionStorage.clearUserState((storedState: any) => {
              const newState: any = {
                ...storedState,
              };
              delete newState.customerState;
              return newState;
            });
            thunkAPI.dispatch(resetLoginState());
            await thunkAPI.dispatch(loginCustomer());
            return await fetchInstance(response.url, {
              headers: {
                Authorization: `Bearer ${getCustomerToken(thunkAPI.getState() as StoreState)}`,
              },
            });
          },
          (response) => {
            // Should intercept when any magento call responds with 401
            return (response.url.includes(environment().magentoUrl) || response.url.includes(environment().rmsUrl)) && response.status === 401;
          }
        );
        isMagentoAuthRefreshInterceptorAdded = true;
      }

      const state = thunkAPI.getState() as StoreState;
      if (!isCustomerLoginSuccess(state)) {
        throw new Error('Customer not logged in');
      }

      return state.customerState.customer.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      // Todo Q. what to do if login fails
    }
  },
  {
    condition: (_arg, { getState }) => {
      const state = getState() as StoreState;
      return state.customerState.customer.status === ActionStatus.idle;
    },
  }
);

/*----- ACTIONS -----*/
export const resetLoginState = createAction('customer/resetLoginState');

/*----- SELECTORS ------*/
export const selectCustomer = (state: StoreState) => state.customerState.customer?.data;
export const isCustomerLoadLoading = (state: StoreState) => state.customerState.customer.status === ActionStatus.loading;
export const isCustomerLoadSuccess = (state: StoreState) => state.customerState.customer.status === ActionStatus.succeeded;
export const isCustomerLoadFailure = (state: StoreState) => state.customerState.customer.status === ActionStatus.failed;

export const isCustomerLoginLoading = (state: StoreState) => state.customerState.login.status === ActionStatus.loading;
export const isCustomerLoginSuccess = (state: StoreState) => state.customerState.login.status === ActionStatus.succeeded;
export const isCustomerLoginFailure = (state: StoreState) => state.customerState.login.status === ActionStatus.failed;

export const getCustomerId = (state: StoreState) => state.customerState.login?.data?.id;
export const getCustomerToken = (state: StoreState) => state.customerState.login?.data?.token;
const getLoginStatus = (state: StoreState) => state.customerState.login.status;
export const getCustomerStatus = (state: StoreState) => state.customerState.customer?.status;

/*----- EXPORT DEFAULT ------*/
export const customerReducer = customerSlice.reducer;

/*----- APIS -----*/
async function magentoLogin() {
    const token = getCookie(COOKIE_MH_TOKEN);
    return fetch(`${environment().magentoUrl}/rest/${environment().checkoutFlow}/V1/person/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Authorization' : `Bearer ${token}`
        },
    }).then((res) => {
        if (res.ok) {
            return res.json();
        } else {
            throw new Error('Error customer login: ' + res.status);
        }
    });
}

export async function personLoginToMagento() {
    if (!getCookie(COOKIE_MH_TOKEN)) {
        throw new Error('User is not logged in. Avoiding person call to magento.')
    }

    const customerinfo = await magentoLogin();

    const refreshMhToken = customerinfo.content?.refreshed_mh_token;
    if (refreshMhToken) {
        addAuthTokenToCookie(refreshMhToken);
    }

    return customerinfo.content;
}

export function isCustomerLoginIdleOrLoading(state: StoreState) {
    return [ActionStatus.idle, ActionStatus.loading].includes(getLoginStatus(state));
}
